import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { extractApiError } from "../lib/errors";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, Zap, MessageSquare, TrendingUp } from "lucide-react";

const LOGO = "/ashra-logo.png";

const SocialBtn = ({ provider, label, icon, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`social-${provider}`}
    className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 px-5 py-3 text-sm font-medium text-zinc-900 transition">
    {icon} {label}
  </button>
);

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(extractApiError(err, "Login failed"));
    } finally { setLoading(false); }
  };

  const requestMagicLink = async () => {
    if (!email) { toast.error("Enter your email above first"); return; }
    setLoading(true);
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${base}/api/auth/magic-link/request`;
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, redirect_path: "/customer" }),
      });
      setMagicSent(true);
      toast.success("Sign-in link sent — check your inbox");
    } catch {
      toast.error("Could not send link");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const comingSoon = (p) => () => toast.info(`${p} login is on the roadmap — use email or Google for now`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 text-zinc-900">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-30 ashra-gradient-bg" />
        <div className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-20 ashra-gradient-bg" />
      </div>
      <div className="relative min-h-screen grid lg:grid-cols-2">
        {/* Left: brand + AI mascot */}
        <div className="flex flex-col justify-between p-8 lg:p-14">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <img src={LOGO} alt="Ashra AI" className="h-12 sm:h-14 w-auto" />
          </Link>
          <div className="max-w-md">
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Turn Conversations
              <br />Into <span className="ashra-gradient-text">Revenue</span>.
            </h1>
            <p className="mt-5 text-zinc-600 text-lg">Your AI revenue team works 24/7 to capture leads, close deals, and maximize every opportunity.</p>
            <ul className="mt-7 space-y-3 text-sm text-zinc-700">
              {[
                { i: Sparkles, t: "AI Captures More Leads" },
                { i: MessageSquare, t: "AI Nurtures Conversations" },
                { i: Zap, t: "AI Closes More Sales" },
                { i: TrendingUp, t: "AI Increases Revenue" },
              ].map(({ i: I, t }) => (
                <li key={t} className="flex items-center gap-2"><span className="h-7 w-7 rounded-lg grid place-items-center bg-violet-100 text-violet-600"><I className="h-3.5 w-3.5" /></span>{t}</li>
              ))}
            </ul>
          </div>
          <div className="text-xs text-zinc-500">Trusted by creators shipping automations daily.</div>
        </div>

        {/* Right: login card */}
        <div className="flex items-center justify-center p-6 lg:p-14">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="w-full max-w-md rounded-3xl bg-white border border-black/5 shadow-2xl shadow-violet-500/10 p-8">
            <h2 className="font-display text-3xl font-bold">Welcome back! <span className="inline-block">👋</span></h2>
            <p className="mt-1 text-sm text-zinc-500">Login to your Ashra AI account</p>

            <div className="mt-6 space-y-2">
              <SocialBtn provider="google" label="Continue with Google" onClick={googleLogin}
                icon={<svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.2-2 13.8-5.3l-6.4-5.4C29.5 34.9 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.4 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.3l6.4 5.4C41.9 34.8 44 29.8 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>} />
              <SocialBtn provider="instagram" label="Continue with Instagram" onClick={comingSoon("Instagram")}
                icon={<svg width="18" height="18" viewBox="0 0 24 24"><defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FEDA75"/><stop offset="25%" stopColor="#FA7E1E"/><stop offset="50%" stopColor="#D62976"/><stop offset="100%" stopColor="#4F5BD5"/></linearGradient></defs><rect width="24" height="24" rx="6" fill="url(#ig)"/><circle cx="12" cy="12" r="4.5" fill="none" stroke="white" strokeWidth="1.8"/><circle cx="17.5" cy="6.5" r="1.2" fill="white"/></svg>} />
              <SocialBtn provider="apple" label="Continue with Apple" onClick={comingSoon("Apple")}
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.44-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.44C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>} />
            </div>

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-400">
              <span className="flex-1 h-px bg-zinc-200" /> or <span className="flex-1 h-px bg-zinc-200" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500">Email address</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@creator.com"
                  className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-violet-500" data-testid="login-email" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-zinc-500">Password</label>
                  <Link to="/forgot-password" className="text-xs text-violet-600" data-testid="login-forgot-link">Forgot?</Link>
                </div>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-violet-500" data-testid="login-password" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full rounded-xl ashra-gradient-bg text-white font-semibold py-3 text-sm hover:opacity-95 transition disabled:opacity-60"
                data-testid="login-submit">
                {loading ? "Signing in…" : "Login"}
              </button>
              <button
                type="button" onClick={requestMagicLink} disabled={loading || magicSent}
                data-testid="login-magic-link"
                className="w-full rounded-xl border border-zinc-200 bg-white text-zinc-700 font-medium py-2.5 text-sm hover:bg-zinc-50 transition disabled:opacity-60"
              >
                {magicSent ? "Sign-in link sent — check your inbox" : "Email me a sign-in link instead"}
              </button>
            </form>

            <p className="mt-6 text-sm text-zinc-500 text-center">
              Don't have an account? <Link to="/signup" className="text-violet-600 font-medium" data-testid="go-signup">Start Free →</Link>
            </p>
            <p className="mt-4 text-[11px] text-zinc-400 text-center">
              By continuing, you agree to Ashra AI's Terms of Service and Privacy Policy
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
